import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { FaPlus, FaIdCard, FaArrowAltCircleUp } from 'react-icons/fa';
import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../services/api';

import { useToast } from '../../hooks/toast';

import Input from '../../components/Input';
import SelectSync from '../../components/SelectSync';
import Loading from '../../components/Loading';
import Modal from '../../components/Modal';

import CartaoSaudeCadastro from '../../components/CartaoSaude/CartaoSaudeCadastro';

import getValidationErrors from '../../utils/getValidationErrors';

import { IOptions } from '../../utils/options';

import { PacienteLinha, PacienteLista } from './styles';

import { useClinica } from '../../hooks/clinica';
import getResponseErrors from '../../utils/getResponseErrors';

interface ICartaoSaude {
  PlaCodigo: number;
  PlaNumero: string;
  PlaDataInicio: Date;
  PlaDiaVencimento: number;
  PlaStatus: boolean;
  PlaSituacao: number;
  PlaClinica: number;
  Dependentes: string;
  PlaDataInicioFormatada: string;
  statusFormatado: string;
  color: string;
}

interface IFormBusca {
  paciente: string;
  numero: number;
  tipo: number;
  clinica: number;
  situacao: number;
}

interface IPaginacao {
  paciente?: string;
  numero?: number;
  tipo?: number;
  clinica?: number;
  situacao: number;
  limite: number;
  pagina: number;
}

const optionsSituacao = [
  { label: 'Ativo', value: '6' },
  { label: 'Atrasado', value: '3' },
  { label: 'Cancelado', value: '1' },
  { label: 'Não Renovado', value: '2' },
  { label: 'Próximos Vencimento', value: '4' },
];

const Paciente: React.FC = () => {
  const { addToast } = useToast();
  const history = useHistory();
  const formRefBusca = useRef<FormHandles>(null);
  const [planos, setPlanos] = useState<ICartaoSaude[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [paginacao, setPaginacao] = useState<IPaginacao>();
  const [optionsTipo, setOptionsTipo] = useState<IOptions[]>([]);
  const { optionsClinica, imagensClinica } = useClinica();
  const [showModalCadastro, setShowModalCadastro] = useState(false);

  const semFoto =
    imagensClinica.find(img => img.nome === 'semFoto')?.arquivo_url || '';

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IOptions[]>('planos/tiposcartao');

        setOptionsTipo(
          response.data.map(opt => {
            return { ...opt, isDisabled: false };
          }),
        );
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar planos',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    loadDados();
  }, [addToast]);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;

        let parametros = '';
        const { clinica, numero, paciente, tipo } = paginacao;
        if (paciente) parametros += `&paciente=${paciente}`;
        if (numero) parametros += `&numero=${numero}`;
        if (tipo) parametros += `&tipo=${tipo}`;
        if (clinica) parametros += `&clinica=${clinica}`;

        const response = await api.get(
          `/planos?&pagina=${paginacao.pagina}&limite=${paginacao.limite}&situacao=${paginacao.situacao}${parametros}`,
        );
        const responseApi: ICartaoSaude[] = response.data[0];
        const total = response.data[1];

        if (total <= paginacao.limite * paginacao.pagina) {
          setPaginacao(undefined);
        }

        const planosFormatados = responseApi.map(pla => {
          return {
            ...pla,
            PlaDataInicioFormatada: format(
              parseISO(pla.PlaDataInicio.toString()),
              'dd/MM/yyyy',
            ),
            color:
              pla.statusFormatado === 'Ativo - Em Dia'
                ? 'alert-primary'
                : 'alert-danger',
          };
        });

        setPlanos(state => {
          return state ? state.concat(planosFormatados) : planosFormatados;
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar planos',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [addToast, paginacao]);

  const handleSubmitBusca = useCallback(async (dados: IFormBusca) => {
    try {
      formRefBusca.current?.setErrors({});
      const schema = Yup.object().shape({
        paciente: Yup.string(),
        numero: Yup.number()
          .nullable()
          .transform(x => x || null),
        tipo: Yup.number()
          .nullable()
          .transform(x => x || null),
        situacao: Yup.string().required(),
        clinica: Yup.number()
          .nullable()
          .transform(x => x || null),
      });

      await schema.validate(dados, {
        abortEarly: false,
      });

      setPlanos(undefined);

      const { paciente, numero, tipo, situacao, clinica } = dados;

      if (situacao) {
        const situacaoNumber = Number(situacao);
        if (situacaoNumber < 1 || situacaoNumber > 6)
          throw new Error('Situação não reconhecida');
      }

      setPaginacao({
        ...(paciente.toString().length > 1 ? { paciente } : {}),
        ...(numero.toString().length > 0 ? { numero: Number(numero) } : {}),
        ...(tipo.toString().length > 0 ? { tipo: Number(tipo) } : {}),
        ...(clinica.toString().length > 0 ? { clinica: Number(clinica) } : {}),
        situacao: Number(situacao),
        pagina: 1,
        limite: 20,
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRefBusca.current?.setErrors(errors);
      }
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      {showModalCadastro && (
        <Modal
          show={showModalCadastro}
          titulo="Novo cartão"
          handleFecharModal={() => setShowModalCadastro(false)}
        >
          <CartaoSaudeCadastro
            handleFecharComponent={
              Codigo => history.push(`cartaosaudedetalhe/${Codigo}`)
              // eslint-disable-next-line react/jsx-curly-newline
            }
          />
        </Modal>
      )}

      <>
        <div className="bg-white border p-2 mb-2">
          <div className="d-flex justify-content-between mb-2">
            <h2 id="topoBusca">Cartão Saúde</h2>
            <div>
              <Button
                type="button"
                variant="success"
                size="sm"
                onClick={() => setShowModalCadastro(true)}
              >
                <FaPlus size={18} /> Novo Cartão
              </Button>
            </div>
          </div>

          <Form
            ref={formRefBusca}
            initialData={{ situacao: '4' }}
            onSubmit={handleSubmitBusca}
          >
            <div className=" mb-2 p-2 bg-white">
              <Row noGutters>
                <Col sm={12} md={5} lg={4} className="pr-1">
                  <Input
                    name="paciente"
                    label="Paciente, Dependente ou Responsável"
                    maxLength={100}
                    minLength={3}
                  />
                </Col>
                <Col sm={6} md={3} lg={1} className="pr-1">
                  <Input
                    name="numero"
                    label="Número"
                    type="number"
                    maxLength={12}
                  />
                </Col>
                <Col sm={6} md={4} lg={2} className="pr-1">
                  <SelectSync
                    name="situacao"
                    label="Situação"
                    placeholder="Selecione"
                    handleSelectChange={handleOnChangeVazio}
                    options={optionsSituacao}
                  />
                </Col>
                <Col sm={12} md={4} lg={2} className="pr-1">
                  <SelectSync
                    name="tipo"
                    label="Tipo"
                    isClearable
                    handleSelectChange={handleOnChangeVazio}
                    options={optionsTipo}
                  />
                </Col>
                <Col sm={6} md={4} lg={2} className="pr-1">
                  <SelectSync
                    name="clinica"
                    label="Clinica"
                    isClearable
                    handleSelectChange={handleOnChangeVazio}
                    options={optionsClinica}
                  />
                </Col>
                <Col sm={12} md={3} lg={1} className="pr-1">
                  <br />
                  <Button
                    type="submit"
                    size="sm"
                    variant="outline-secondary mt-1"
                  >
                    BUSCAR
                  </Button>
                </Col>
              </Row>
            </div>
          </Form>
        </div>

        <div className="bg-white border p-2">
          {planos ? (
            planos.map(cartao => (
              <Link
                to={`cartaosaudedetalhe/${cartao.PlaCodigo}`}
                className="text-dark"
                style={{ textDecoration: 'none' }}
                key={`cartao-${cartao.PlaCodigo}`}
              >
                <PacienteLinha className="row">
                  <div className="col-md-5">
                    <PacienteLista>
                      <img src={semFoto} alt={cartao.PlaNumero} />
                      {cartao.Dependentes}
                    </PacienteLista>
                  </div>
                  <div className="col-md-2 col-lg-1">
                    <PacienteLista>
                      <span>Número: </span> {cartao.PlaNumero}
                    </PacienteLista>
                  </div>
                  <div className="col-md-2 col-lg-4">
                    <PacienteLista>
                      <div style={{ flex: 1 }}>
                        <span>Data Início: </span>
                        {cartao.PlaDataInicioFormatada}
                      </div>
                      <div>
                        <span>Dia Vencimento: </span> {cartao.PlaDiaVencimento}
                      </div>
                    </PacienteLista>
                  </div>
                  <div className="col-md-3 col-lg-2">
                    <div
                      className={`alert ${cartao.color} mb-0 d-block`}
                      style={{ maxWidth: '200px' }}
                    >
                      <FaIdCard size={13} /> {cartao.statusFormatado}
                    </div>
                  </div>
                </PacienteLinha>
              </Link>
            ))
          ) : (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '250px' }}
            >
              <h5>
                Pesquise pelo Nome do Titular, Responsável dependentes, pelo
                número do cartão e outros
              </h5>
            </div>
          )}

          {paginacao && (
            <div className="d-block text-center py-3">
              <Button
                type="button"
                variant="outline-secondary"
                className="rounded-0"
                onClick={() =>
                  // eslint-disable-next-line prettier/prettier
                  setPaginacao({ ...paginacao, pagina: paginacao.pagina + 1 })}
              >
                Carregar mais
              </Button>
            </div>
          )}

          {planos && (
            <a
              href="#topoBusca"
              className="btn btn-primary"
              style={{
                position: 'fixed',
                bottom: '20px',
                right: '35px',
                opacity: 0.8,
              }}
            >
              <FaArrowAltCircleUp size={20} />
            </a>
          )}
        </div>
      </>
    </Container>
  );
};

export default Paciente;
